import {
  PROJECT_FOLDER_TYPE,
  type ProjectFile,
  type ProjectFileData,
  type ProjectFileStructure,
} from './types';
import {isNullOrUndefined} from 'utils/general';

/**
 * File names that count as a project's entry point, in preference order.
 *
 * `main.py` is not a display convention — it is the file the code runners
 * actually execute. Every unit-test runner resolves it by name (see the
 * `main.py` lookups in `assignments/unitTest/`), so the entry file is also the
 * file a learner is nearly always meant to edit first.
 */
export const ENTRY_FILE_NAMES = ['main.py', 'index.html'] as const;

/** The `format` of a project file that holds an image rather than text. */
const IMAGE_FORMAT = 'image';

/**
 * Returns the files at the effective top level of a file structure.
 *
 * Accepts either shape the app stores files in: a single root folder wrapping
 * the files (how `projects/{id}.files` is written), or a bare list of files
 * (how starter files are held before they are wrapped in a folder). Nested
 * folders are NOT descended into, so a nested `main.py` can never outrank a
 * top-level one.
 *
 * @param fileStructure The structure to read, in either shape.
 * @return The top-level entries, files and folders alike.
 */
function getTopLevelEntries(
  fileStructure: ProjectFileStructure,
): ProjectFileStructure {
  if (!Array.isArray(fileStructure) || fileStructure.length === 0) {
    return [];
  }
  const firstEntry = fileStructure[0];
  if (firstEntry?.type === PROJECT_FOLDER_TYPE) {
    return Array.isArray(firstEntry.files) ? firstEntry.files : [];
  }
  return fileStructure;
}

/**
 * Picks the file a project should open on, in this order: `main.py`,
 * `index.html`, then the first Python file.
 *
 * Returns `null` rather than reaching for some other file when none of those
 * exist, matching what the load path did before this module existed.
 * `RunUnitTestButton` treats that `null` as "this project has no main file" and
 * disables itself, so widening the cascade would quietly re-enable the
 * unit-test pipeline for projects that have nothing for it to run.
 *
 * This is the single source of truth for "which file is open" — used when a
 * project is first created, when it is loaded, and when it is reverted to
 * starter code. It deliberately does not depend on the ORDER of the files it
 * is given, because for starter files that order is NONDETERMINISTIC: it comes
 * from the field order of an assignment's `docs/starterCode` map, and Firestore
 * does not guarantee one. Reading prod's `guessmyanimal` starter code eight
 * times through the same `getDocs` call the IDE uses returned `animal.py` first
 * five times and `main.py` first three times. So reordering an assignment's
 * starter code cannot fix "the wrong file opened" — only choosing by name can.
 * See [#4039](https://github.com/CodeinPlace/cip/issues/4039).
 *
 * @param fileStructure The project's files, either as a single root folder or
 *   as a bare list of files. Nested folders are not descended into.
 * @return A shallow copy of the chosen file, or `null` when there is no
 *   `main.py`, `index.html` or other Python file at the top level.
 */
export function pickEntryFile(
  fileStructure: ProjectFileStructure,
): ProjectFile | null {
  const files = getEntryFileCandidates(fileStructure);

  for (const entryFileName of ENTRY_FILE_NAMES) {
    const namedFile = files.find(file => file.name === entryFileName);
    if (namedFile) {
      return {...namedFile};
    }
  }

  const pythonFile = files.find(file => file.name?.endsWith('.py'));
  return pythonFile ? {...pythonFile} : null;
}

/**
 * The entries of a file structure that could be opened in the editor.
 *
 * Excludes folders rather than requiring `type === 'file'`, so an entry with a
 * missing or malformed `type` still matches by name — the load path this module
 * replaced matched on name alone, and narrowing that could strand a project on
 * an empty editor. Folders ARE excluded, which that old name-only match failed
 * to do: it would happily return a folder named `helpers.py`. Nullish holes are
 * dropped so callers can read `.name` without guarding.
 *
 * @param fileStructure The structure to read, in either shape
 *   {@link getTopLevelEntries} accepts.
 * @return The candidate files, folders and nullish entries removed.
 */
function getEntryFileCandidates(
  fileStructure: ProjectFileStructure,
): ProjectFile[] {
  return getTopLevelEntries(fileStructure).filter(
    (entry): entry is ProjectFile =>
      !isNullOrUndefined(entry) && entry.type !== PROJECT_FOLDER_TYPE,
  );
}

/**
 * Moves the entry file to the front of a flat list of files, preserving the
 * relative order of everything else.
 *
 * The file tree renders files in stored order with no sort of its own
 * (`ide/Files/FileTree.jsx`), so this is what makes `main.py` the first thing
 * a learner sees in a multi-file project. It also means the entry file IS
 * index 0, which keeps callers that reach for the first file honest.
 *
 * @param files A flat list of files, e.g. the starter files destined for a
 *   project's root folder. A structure whose first entry is a folder is
 *   returned unchanged, since reordering across a folder boundary is not
 *   meaningful.
 * @return A new list with the entry file first, or the original list when
 *   there is no entry file or it is already first.
 */
export function withEntryFileFirst(
  files: ProjectFileStructure,
): ProjectFileStructure {
  const entryFile = pickEntryFile(files);
  if (!entryFile) {
    return files;
  }
  // Matches on id alone. Re-checking `type === 'file'` here would fail to find
  // the malformed-`type` entries `pickEntryFile` deliberately accepts, and the
  // resulting -1 would be indistinguishable from "already first" — leaving
  // `buildStarterProjectState` reporting an `openFile` that is not the file its
  // own tree lists first.
  const entryIndex = files.findIndex(
    entry =>
      !isNullOrUndefined(entry) &&
      entry.type !== PROJECT_FOLDER_TYPE &&
      entry.id === entryFile.id,
  );
  if (entryIndex === -1 || entryIndex === 0) {
    return files;
  }
  return [
    files[entryIndex],
    ...files.slice(0, entryIndex),
    ...files.slice(entryIndex + 1),
  ];
}

/**
 * The IDE state for a project that was just seeded (or re-seeded) from an
 * assignment's starter code: the file tree to show, and the file to open.
 */
export interface StarterProjectState {
  /** The project's file structure, as a single root folder. */
  fileStructure: ProjectFileStructure;
  /**
   * The file the editor should open, and the value to persist as the project's
   * `lastOpenedFile`. `null` only when there are no files at all.
   */
  openFile: ProjectFileData | null;
}

/**
 * Decides what the IDE shows after a project is seeded from starter code —
 * the file tree, and which file is open.
 *
 * This exists as a separate function so the decision is testable without
 * mounting the IDE. The regression it guards is
 * [#4039](https://github.com/CodeinPlace/cip/issues/4039): the Restart handler
 * used to open `starterFiles[0]`, and since that list arrives in a
 * nondeterministic order (see {@link pickEntryFile}), it opened an arbitrary
 * file on every restart. Any reintroduction of positional selection fails
 * `entryFile.test.ts`, which asserts the choice is identical across every
 * permutation of the same files.
 *
 * @param starterFiles A FLAT list of files, in whatever order the assignment's
 *   starter-code map yielded — i.e. what `createAssnStarterCodeFiles` returns.
 *   Not mutated. Unlike {@link pickEntryFile} and {@link withEntryFileFirst},
 *   which accept either shape, this always WRAPS its input in a new root
 *   folder, so passing an already-wrapped structure (`projectData.files`) would
 *   nest a folder inside a folder and the file tree would persist that.
 * @param projectTitle Title for the root folder. Pass the assignment title; an
 *   undefined value is preserved as-is so callers keep their existing behavior
 *   for untitled assignments.
 * @return The file tree (entry file first) and the file to open. Unlike
 *   {@link pickEntryFile}, `openFile` falls back to the first file when no entry
 *   file is present, and so is `null` ONLY for an empty list. A seeded project
 *   must always end up with an `openFile` to persist as `lastOpenedFile`:
 *   skipping that write leaves the PREVIOUS `lastOpenedFile` in place, pointing
 *   at a code doc from before the reseed (Restart mints new file ids and does not
 *   delete the old docs), so the next load would show the user their old code
 *   beside the new file tree — a Restart that looks like it never happened.
 */
export function buildStarterProjectState({
  starterFiles,
  projectTitle,
}: {
  starterFiles: ProjectFileStructure;
  projectTitle?: string;
}): StarterProjectState {
  const orderedFiles = withEntryFileFirst(starterFiles);
  // The fallback only bites for starter code with no `main.py`, `index.html` or
  // other `.py` file. There, opening (and storing) some editable file beats
  // leaving a stale `lastOpenedFile` behind — see the @return note. Images are
  // skipped so the editor never lands on a picture; if that leaves nothing, the
  // project genuinely has no file to open.
  const openFile =
    pickEntryFile(orderedFiles) ??
    getEntryFileCandidates(orderedFiles).find(
      file => file.format !== IMAGE_FORMAT,
    ) ??
    null;
  return {
    fileStructure: [
      {
        type: PROJECT_FOLDER_TYPE,
        name: projectTitle,
        files: orderedFiles,
      },
    ],
    openFile: openFile ? {id: openFile.id, name: openFile.name} : null,
  };
}
